import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';

import {
    normalizeScopeTypes,
    type ScratchpadSearch,
    serializeScopeTypes,
} from '@apps/web/features/admin_agent_scratchpad/model/search_params';
import { ScopeBadge } from '@apps/web/features/admin_agent_scratchpad/ui/scope_badge';
import { RelativeTimestamp } from '@apps/web/features/admin_agent_scratchpad/ui/timestamp';
import { VolatilityGlyph } from '@apps/web/features/admin_agent_scratchpad/ui/volatility_glyph';
import { cn } from '@apps/web/lib/utils';
import {
    type AdminScratchpadNoteSummaryRead,
    type components,
} from '@libraries/typescript/api_client';

const MAX_INLINE_TAGS = 3;

/**
 * Single row in the vault list. The whole card is clickable via an absolutely
 * positioned overlay link, but interactive elements (the tooltip triggers on
 * the timestamps) stay OUTSIDE the anchor so `<a><button>` never happens.
 * Clicks on the timestamps still hit the tooltip button because we lift its
 * z-index above the overlay.
 */
export function ScratchpadNoteRow({
    note,
    linkSearch,
    highlighted,
}: {
    readonly note: AdminScratchpadNoteSummaryRead;
    readonly linkSearch: ScratchpadSearch;
    readonly highlighted: boolean;
}) {
    const isRetired = note.retired_at !== null;
    return (
        <div
            className={cn(
                'group border-border/40 hover:border-border hover:bg-muted/40 relative flex flex-col gap-1.5 border-b px-4 py-3 transition-colors last:border-b-0',
                highlighted && 'ring-primary/60 ring-1 ring-inset',
                isRetired && 'opacity-70',
            )}
        >
            <Link
                to="/admin/agent/scratchpad/$note-id"
                params={{ 'note-id': note.id }}
                search={{
                    ...linkSearch,
                    scope_type: serializeScopeTypes(
                        normalizeScopeTypes(linkSearch),
                    ),
                    revisions_page: 1,
                }}
                aria-label={`Open scratchpad note ${note.title}`}
                className="absolute inset-0 z-0 rounded-md focus-visible:ring-1 focus-visible:outline-none focus-visible:ring-inset"
            />
            <span className="pointer-events-none relative z-10 flex flex-wrap items-center gap-1.5">
                <VolatilityGlyph volatility={note.volatility} />
                <span
                    className={cn(
                        'text-foreground/95 font-medium tracking-tight',
                        isRetired && 'line-through decoration-1',
                    )}
                >
                    {note.title}
                </span>
                <span className="text-muted-foreground/60 text-[10px] tracking-wider uppercase">
                    v{note.version}
                </span>
                {isRetired ? (
                    <span className="rounded-md bg-rose-100/70 px-1.5 py-0.5 text-[10px] font-medium tracking-wider text-rose-800 uppercase dark:bg-rose-900/40 dark:text-rose-200">
                        Retired
                    </span>
                ) : null}
                <ChevronRight className="text-muted-foreground/50 ml-auto h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
            </span>
            <p className="text-muted-foreground pointer-events-none relative z-10 max-w-prose text-sm leading-snug">
                {note.one_liner}
            </p>
            {note.snippet ? <SearchSnippet snippet={note.snippet} /> : null}
            <span className="text-muted-foreground/85 relative z-10 flex flex-wrap items-center gap-2 pt-1 text-[11px]">
                <span className="pointer-events-auto">
                    <ScopeBadge
                        scopeType={note.scope_type}
                        scopeId={note.scope_id}
                        label={note.scope.organization_name}
                    />
                </span>
                {note.tags.slice(0, MAX_INLINE_TAGS).map((tag) => (
                    <span
                        key={tag}
                        className="bg-muted/70 text-foreground/80 pointer-events-none rounded-sm px-1.5 py-0.5 text-[10px] font-medium"
                    >
                        #{tag}
                    </span>
                ))}
                {note.tags.length > MAX_INLINE_TAGS ? (
                    <span className="text-muted-foreground/60 pointer-events-none">
                        +{note.tags.length - MAX_INLINE_TAGS}
                    </span>
                ) : null}
                <span className="pointer-events-auto ml-auto flex items-center gap-3">
                    <RelativeTimestamp
                        iso={note.last_verified_at}
                        prefix="verified"
                        organizationTimezone={note.scope.organization_timezone}
                    />
                    <RelativeTimestamp
                        iso={note.updated_at}
                        prefix="updated"
                        organizationTimezone={note.scope.organization_timezone}
                    />
                </span>
            </span>
        </div>
    );
}

/**
 * Postgres ts_headline snippet — safe HTML with only <b> around matches.
 * Rendered inline so the reader can eyeball the hit without opening the note.
 */
function SearchSnippet({ snippet }: { readonly snippet: string }) {
    // ts_headline returns `<b>match</b>` markup; render as text but bold hits.
    const parts = snippet.split(/(<b>[^<]*<\/b>)/g);
    return (
        <p className="text-foreground/80 pointer-events-none relative z-10 max-w-prose text-[13px] leading-snug">
            {parts.map((part, index) =>
                part.startsWith('<b>') ? (
                    <mark
                        key={index}
                        className="bg-amber-200/60 px-0.5 font-medium text-inherit dark:bg-amber-400/30"
                    >
                        {part.replace(/^<b>|<\/b>$/g, '')}
                    </mark>
                ) : (
                    <span key={index}>{part}</span>
                ),
            )}
        </p>
    );
}

// Kept exported so tests can inspect the schema mapping.
export type ScratchpadNoteRowProps = {
    readonly note: components['schemas']['AdminScratchpadNoteSummaryRead'];
    readonly linkSearch: ScratchpadSearch;
    readonly highlighted: boolean;
};
